import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, LayoutGrid, Table as TableIcon, Search, Undo2 } from "lucide-react";
import { DIVISIONS, LEAD_STATUSES, ADMISSION_MODES, divisionLabel, statusLabel } from "@/lib/roles";
import LeadDrawer from "@/components/LeadDrawer";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

const STATUS_COLOR = {
  new:       { bg: "#E0F2FE", fg: "#0284C7", dot: "#0EA5FF" },
  contacted: { bg: "#DBEAFE", fg: "#1D4ED8", dot: "#1D4ED8" },
  qualified: { bg: "#EDE9FE", fg: "#6D28D9", dot: "#6D28D9" },
  proposal:  { bg: "#FEF3C7", fg: "#B45309", dot: "#F59E0B" },
  won:       { bg: "#DCFCE7", fg: "#15803D", dot: "#5FBF3A" },
  lost:      { bg: "#FEE2E2", fg: "#B91C1C", dot: "#EF4444" },
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [view, setView] = useState("kanban");
  const [scope, setScope] = useState("active");
  const [filters, setFilters] = useState({ division: "all", status: "all", q: "" });
  const [drawerId, setDrawerId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (filters.division !== "all") params.division = filters.division;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.q) params.q = filters.q;
    if (scope === "archived") params.only_archived = true;
    const r = await api.get("/leads", { params });
    setLeads(r.data);
  }, [filters.division, filters.status, filters.q, scope]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);

  const restore = async (id, e) => {
    e?.stopPropagation();
    await api.post(`/leads/${id}/restore`);
    toast.success("Restored");
    load();
  };

  const byStatus = useMemo(() => {
    const map = Object.fromEntries(LEAD_STATUSES.map(s => [s.value, []]));
    for (const l of leads) (map[l.status] ||= []).push(l);
    return map;
  }, [leads]);

  const userName = (id) => users.find(u => u.id === id)?.name || "Unassigned";

  return (
    <AppLayout title="Leads">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2 bg-white border border-[var(--miro-border)] rounded-lg px-3 py-2 flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="text-[var(--miro-text-muted)]"/>
          <input
            data-testid="leads-search-input"
            className="bg-transparent outline-none text-sm flex-1"
            placeholder="Search leads by name, email…"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          />
        </div>

        <Select value={filters.division} onValueChange={(v) => setFilters({ ...filters, division: v })}>
          <SelectTrigger data-testid="leads-filter-division" className="w-40 bg-white"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All divisions</SelectItem>
            {DIVISIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger data-testid="leads-filter-status" className="w-36 bg-white"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {LEAD_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2">
          <div className="flex bg-white border border-[var(--miro-border)] rounded-lg p-0.5">
            {[["active","Active"],["archived","Archived"]].map(([v,l]) => (
              <button key={v} data-testid={`leads-scope-${v}`}
                      onClick={() => setScope(v)}
                      className={`h-8 px-3 rounded-md text-xs font-semibold transition-colors ${scope === v ? "bg-[var(--miro-navy)] text-white" : "text-[var(--miro-text-secondary)]"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="flex bg-white border border-[var(--miro-border)] rounded-lg p-0.5">
            <button
              data-testid="leads-view-kanban"
              onClick={() => setView("kanban")}
              className={`h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors ${view === "kanban" ? "bg-[var(--miro-navy)] text-white" : "text-[var(--miro-text-secondary)]"}`}
            >
              <LayoutGrid size={14}/> Kanban
            </button>
            <button
              data-testid="leads-view-table"
              onClick={() => setView("table")}
              className={`h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors ${view === "table" ? "bg-[var(--miro-navy)] text-white" : "text-[var(--miro-text-secondary)]"}`}
            >
              <TableIcon size={14}/> Table
            </button>
          </div>

          <NewLeadDialog open={newOpen} setOpen={setNewOpen} users={users} onCreated={load}/>
        </div>
      </div>

      {view === "kanban" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {LEAD_STATUSES.map(s => (
            <div key={s.value} data-testid={`kanban-col-${s.value}`} className="kanban-col">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLOR[s.value].dot }}/>
                  <div className="font-display font-bold text-sm text-[var(--miro-navy)]">{s.label}</div>
                </div>
                <span className="text-xs font-mono text-[var(--miro-text-muted)]">{byStatus[s.value]?.length || 0}</span>
              </div>
              <div className="space-y-2">
                {(byStatus[s.value] || []).map(l => (
                  <div
                    key={l.id}
                    data-testid={`kanban-card-${l.id}`}
                    onClick={() => setDrawerId(l.id)}
                    className="miro-card p-3 cursor-pointer"
                  >
                    <div className="font-semibold text-sm text-[var(--miro-navy)] truncate">{l.name}</div>
                    <div className="text-xs text-[var(--miro-text-secondary)] truncate mt-0.5">{l.interest || "—"}</div>
                    <div className="flex items-center justify-between mt-3 text-[11px] text-[var(--miro-text-muted)]">
                      <span>{divisionLabel(l.division)}</span>
                      <span className="font-mono text-[var(--miro-navy)]">₹{(l.estimated_value || 0).toLocaleString("en-IN")}</span>
                    </div>
                    <div className="mt-2 text-[10px] text-[var(--miro-text-muted)] flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[var(--miro-blue)]"/> {userName(l.assigned_to)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="leads-table" className="w-full text-sm">
              <thead className="bg-[var(--miro-bg)] text-[var(--miro-text-secondary)]">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Division</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Assigned</th>
                  <th className="px-4 py-3 font-semibold">Value</th>
                  <th className="px-4 py-3 font-semibold">Created</th>
                  {scope === "archived" && <th className="px-4 py-3"/>}
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan={scope === "archived" ? 7 : 6} className="px-4 py-12 text-center text-[var(--miro-text-muted)]">No leads yet.</td></tr>
                )}
                {leads.map(l => (
                  <tr
                    key={l.id}
                    data-testid={`leads-row-${l.id}`}
                    onClick={() => setDrawerId(l.id)}
                    className="border-t border-[var(--miro-border)] cursor-pointer hover:bg-[var(--miro-bg)] transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[var(--miro-navy)]">{l.name}</div>
                      <div className="text-xs text-[var(--miro-text-muted)]">{l.email || l.phone}</div>
                    </td>
                    <td className="px-4 py-3 text-[var(--miro-text-secondary)]">{divisionLabel(l.division)}</td>
                    <td className="px-4 py-3">
                      <Badge style={{ background: STATUS_COLOR[l.status].bg, color: STATUS_COLOR[l.status].fg }} className="border-0 font-semibold">
                        {statusLabel(l.status)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-[var(--miro-text-secondary)]">{userName(l.assigned_to)}</td>
                    <td className="px-4 py-3 font-mono text-[var(--miro-navy)]">₹{(l.estimated_value || 0).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-xs text-[var(--miro-text-muted)]">
                      {formatDistanceToNow(new Date(l.created_at), { addSuffix: true })}
                    </td>
                    {scope === "archived" && (
                      <td className="px-4 py-3 text-right">
                        <button data-testid={`lead-restore-${l.id}`} onClick={(e) => restore(l.id, e)}
                                className="text-[var(--miro-text-muted)] hover:text-green-600" title="Restore">
                          <Undo2 size={15}/>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <LeadDrawer
        leadId={drawerId}
        open={!!drawerId}
        onClose={() => setDrawerId(null)}
        onChanged={load}
      />
    </AppLayout>
  );
}

function NewLeadDialog({ open, setOpen, users, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", division: "academy", status: "new",
    source: "manual", interest: "", estimated_value: 0, assigned_to: "auto",
    admission_type: "", admission_program: "", admission_mode: "",
    admission_duration: "", admission_fee: "",
  });
  const [saving, setSaving] = useState(false);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isAdmissions = form.division === "indian_admissions";

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, estimated_value: Number(form.estimated_value) || 0 };
      if (payload.assigned_to === "auto") delete payload.assigned_to;
      // Only send admission_* when Indian Admissions is selected, and coerce
      // empty strings to undefined so we don't store noise.
      const admKeys = ["admission_type","admission_program","admission_mode","admission_duration","admission_fee"];
      if (isAdmissions) {
        payload.admission_fee = payload.admission_fee === "" || payload.admission_fee == null
          ? null : Number(payload.admission_fee);
        admKeys.forEach(k => { if (payload[k] === "") payload[k] = null; });
      } else {
        admKeys.forEach(k => delete payload[k]);
      }
      await api.post("/leads", payload);
      toast.success("Lead created");
      setOpen(false);
      setForm({ ...form, name: "", email: "", phone: "", interest: "", estimated_value: 0,
        admission_type: "", admission_program: "", admission_mode: "",
        admission_duration: "", admission_fee: "" });
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-lead-btn" className="font-semibold" style={{ background: "var(--miro-navy)" }}>
          <Plus size={16} className="mr-1"/> New lead
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="new-lead-dialog" className="bg-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New lead</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Name *</Label>
            <Input data-testid="new-lead-name" value={form.name} onChange={(e) => upd("name", e.target.value)} className="mt-1"/>
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="new-lead-email" type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} className="mt-1"/>
          </div>
          <div>
            <Label>Phone</Label>
            <Input data-testid="new-lead-phone" value={form.phone} onChange={(e) => upd("phone", e.target.value)} className="mt-1"/>
          </div>
          <div>
            <Label>Division</Label>
            <Select value={form.division} onValueChange={(v) => upd("division", v)}>
              <SelectTrigger data-testid="new-lead-division" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                {DIVISIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => upd("status", v)}>
              <SelectTrigger data-testid="new-lead-status" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                {LEAD_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Source</Label>
            <Select value={form.source} onValueChange={(v) => upd("source", v)}>
              <SelectTrigger data-testid="new-lead-source" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="public_form">Public form</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="referral">Referral</SelectItem>
                <SelectItem value="ads">Ads</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Est. value (₹)</Label>
            <Input data-testid="new-lead-value" type="number" min={0} value={form.estimated_value}
                   onChange={(e) => upd("estimated_value", e.target.value)} className="mt-1"/>
          </div>
          <div className="col-span-2">
            <Label>Assigned to</Label>
            <Select value={form.assigned_to} onValueChange={(v) => upd("assigned_to", v)}>
              <SelectTrigger data-testid="new-lead-assignee" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-assign by division</SelectItem>
                {users
                  .filter(u => u.role === "super_admin" || u.role === "employee")
                  .map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role.replace(/_/g," ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1">
              Only super admin and employees can be assigned leads.
            </p>
          </div>
          <div className="col-span-2">
            <Label>Interest</Label>
            <Textarea data-testid="new-lead-interest" rows={2} value={form.interest} onChange={(e) => upd("interest", e.target.value)} className="mt-1"/>
          </div>

          {isAdmissions && (
            <div className="col-span-2 rounded-lg border border-[var(--miro-border)] bg-[var(--miro-bg)] p-3 space-y-3">
              <div className="miro-overline">Indian Admission details</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Admission type</Label>
                  <Input
                    data-testid="new-lead-admission-type"
                    placeholder="e.g. MBA, MBBS, BTech"
                    value={form.admission_type}
                    onChange={(e) => upd("admission_type", e.target.value)}
                    className="mt-1 bg-white"
                  />
                </div>
                <div>
                  <Label>Program / Institution</Label>
                  <Input
                    data-testid="new-lead-admission-program"
                    placeholder="e.g. Kalinga MBA – Marketing"
                    value={form.admission_program}
                    onChange={(e) => upd("admission_program", e.target.value)}
                    className="mt-1 bg-white"
                  />
                </div>
                <div>
                  <Label>Mode</Label>
                  <Select value={form.admission_mode || "unset"} onValueChange={(v) => upd("admission_mode", v === "unset" ? "" : v)}>
                    <SelectTrigger data-testid="new-lead-admission-mode" className="mt-1 bg-white"><SelectValue placeholder="Select…"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unset">—</SelectItem>
                      {ADMISSION_MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Duration</Label>
                  <Input
                    data-testid="new-lead-admission-duration"
                    placeholder="e.g. 2 years"
                    value={form.admission_duration}
                    onChange={(e) => upd("admission_duration", e.target.value)}
                    className="mt-1 bg-white"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Fee (₹)</Label>
                  <Input
                    data-testid="new-lead-admission-fee"
                    type="number"
                    min={0}
                    value={form.admission_fee}
                    onChange={(e) => upd("admission_fee", e.target.value)}
                    className="mt-1 bg-white"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="new-lead-submit" onClick={submit} disabled={saving || !form.name.trim()}
                  style={{ background: "var(--miro-navy)" }}>
            {saving ? "Creating…" : "Create lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
